import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Switch } from './Switch.js'

export interface InstantSwitchProps {
  /** What the server currently says. */
  checked: boolean
  /** What it is called, for a screen reader and for the failure message. */
  label: string
  /** The write. Resolve when it landed, reject when it did not. */
  onWrite: (on: boolean) => Promise<unknown>
  disabled?: boolean
  className?: string
}

/**
 * A switch bound to the server that still moves the moment it is pressed.
 *
 * `Switch` is controlled: it shows `checked` and nothing else. So a toggle
 * wired straight to a mutation — `checked={row.is_active}` with
 * `onCheckedChange={() => save(...)}` — **does not move when you press it**.
 * It sits still through the round trip and the refetch behind it, and only
 * then jumps. On a slow connection that is a second or more of a control that
 * looks broken, and the natural response is to press it again.
 *
 * Worse, when the write fails nothing at all happens: no movement, no
 * message. Eight switches on this desk behaved that way, including the four
 * that decide which notifications every member of the platform receives.
 *
 * So this one holds a wish. It shows the position you asked for straight
 * away, drops the wish once the server agrees, and if the write is refused it
 * slides back and says why. Nothing is said on success — the switch being
 * where you put it is the confirmation, and a toast for every flick of a
 * settings page is noise.
 */
export function InstantSwitch({
  checked,
  label,
  onWrite,
  disabled,
  className,
}: InstantSwitchProps) {
  const [wish, setWish] = useState<boolean | null>(null)

  /* The server caught up, so the switch goes back to being told what to show.
     Keeping the wish would freeze the switch against any later change made
     somewhere else — another tab, another person on the desk. */
  useEffect(() => {
    if (wish !== null && wish === checked) setWish(null)
  }, [checked, wish])

  return (
    <Switch
      aria-label={label}
      checked={wish ?? checked}
      disabled={disabled}
      {...(className ? { className } : {})}
      onCheckedChange={(on) => {
        setWish(on)
        void onWrite(on).catch(() => {
          setWish(null)
          toast.error(`“${label}” could not be changed. Nothing was saved.`)
        })
      }}
    />
  )
}
